import { useQuery } from "@tanstack/react-query";
import { useNotify } from "ra-core";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getSupabaseClient } from "../../providers/supabase/supabase";
import { useSaveDeal, type DealRow } from "./boardData";
import { HelpTooltip } from "../help/HelpTooltip";

/**
 * Eliminar con confirmación (PRD B.2). Sugiere "Marcar como perdida" porque así
 * cuenta en las tasas; eliminar es para lo creado por error.
 */
export const DeleteDealDialog = ({
  deal,
  lostStage,
  onClose,
  onLose,
}: {
  deal: DealRow | null;
  lostStage?: string;
  onClose: () => void;
  onLose: (deal: DealRow) => void;
}) => {
  const notify = useNotify();
  const { refresh } = useSaveDeal();
  const { data: pending } = useQuery({
    queryKey: ["deal-pending-tasks", deal?.id],
    enabled: !!deal,
    queryFn: async () => {
      const { count } = await getSupabaseClient()
        .from("tasks")
        .select("id", { count: "exact", head: true })
        .eq("deal_id", deal!.id)
        .is("done_date", null);
      return count ?? 0;
    },
  });
  if (!deal) return null;
  const canLose = !!lostStage && deal.stage !== lostStage;

  const remove = async () => {
    // tareas: se borran solas (FK on delete cascade); notas se quedan en la empresa
    const { error } = await getSupabaseClient()
      .from("deals")
      .delete()
      .eq("id", deal.id);
    if (error) {
      notify(error.message, { type: "error" });
      return;
    }
    refresh();
    notify(`${deal.key} eliminada`, { type: "success" });
    onClose();
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-1">
            ¿Eliminar {deal.key}?
            <HelpTooltip topic="deal.delete" />
          </DialogTitle>
          <DialogDescription>{deal.company_name}</DialogDescription>
        </DialogHeader>
        <p className="text-sm">
          Se borran la oportunidad y {pending ?? 0} tarea
          {pending === 1 ? "" : "s"} pendiente{pending === 1 ? "" : "s"}. Las
          llamadas y notas se quedan en la ficha de la empresa.
        </p>
        {canLose && (
          <p className="rounded-lg bg-secondary p-3 text-sm">
            <b>Mejor márcala como Perdida</b> si el cliente dijo que no: así
            cuenta en tus tasas de cierre y el termómetro aprende. Elimina solo
            lo que creaste por error.
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          {canLose && (
            <Button
              variant="outline"
              onClick={() => {
                onClose();
                onLose(deal);
              }}
            >
              Marcar como perdida
            </Button>
          )}
          <Button variant="destructive" onClick={remove}>
            Eliminar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
